import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { IconTile, type IconTone, type KitIcon } from "./IconTile";
import { foldKey, readFold, useFoldPage, writeFold } from "./FoldPage";

export interface FoldCardProps {
  /** The header's icon tile. */
  icon?: KitIcon;
  tone?: IconTone;
  title: ReactNode;
  /** One line under the title, shown open or closed: the state in a few words ("Weekdays 08:00–17:00 UK"). */
  summary?: ReactNode;
  /** An optional status chip on the right of the header (StatusChip, a count, "Most used"). */
  chip?: ReactNode;
  /** A mono time or reference on the right, before the chevron. */
  meta?: ReactNode;
  /** Buttons or links on the right of the header, outside the toggle: visible open or closed, and pressing one never folds the card. */
  actions?: ReactNode;
  /** Controlled: pass open and onToggle together. */
  open?: boolean;
  onToggle?: (open: boolean) => void;
  /** Uncontrolled: whether it starts open (when this browser has no remembered choice). Open the page's first card. */
  defaultOpen?: boolean;
  /** The card holds things waiting for the owner: it starts open, shows the waiting mark, and an old "closed" made before anything waited does not hide it. */
  waiting?: boolean;
  /** When this turns true the card opens (a link points into it, like ?task=). */
  reveal?: boolean;
  /** Remember the open/closed choice in this browser (default true when the card has an id). Turn off for short-lived items (one task, one batch). */
  persist?: boolean;
  /** "card" stands alone with its own border; "row" sits flush inside a list card (FoldList) with a top rule. */
  variant?: "card" | "row";
  /** The heading level of the title, so the page outline stays right. */
  level?: 2 | 3 | 4;
  /** With an id the card joins the page's set: its choice is remembered and a link to #id opens it. Unique on the page. */
  id?: string;
  /** Always open, with no chevron: for pages that show every card's content at once (the Campaigns pages, 31 Oct 2026). */
  fixed?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * FoldCard: the fold-out card. Header row = icon tile, title, one-line summary, optional chip and meta, chevron, and
 * optional actions beside it. The header is a real button inside a heading with aria-expanded and aria-controls; the
 * body is hidden when closed. Works controlled (open + onToggle) or uncontrolled (defaultOpen / waiting). With an id it
 * remembers its choice per page in this browser (FoldPage.tsx).
 * New code uses Card (the same thing with id and subtitle required).
 */
export function FoldCard({
  icon,
  tone,
  title,
  summary,
  chip,
  meta,
  actions,
  open,
  onToggle,
  defaultOpen,
  waiting = false,
  reveal = false,
  persist = true,
  variant = "card",
  level = 3,
  id,
  fixed = false,
  className,
  children,
}: FoldCardProps) {
  const page = useFoldPage();
  const path = page?.path ?? (typeof window !== "undefined" ? window.location.pathname : "/");
  const key = id && persist ? foldKey(path, id) : null;
  const fallback = defaultOpen ?? waiting;
  const [own, setOwn] = useState<boolean>(() => (key ? (readFold(key, waiting) ?? fallback) : fallback));
  const controlled = open !== undefined;
  const isOpen = fixed || (controlled ? open : own);
  const auto = useId();
  const bodyId = `${id ?? auto}-body`;
  const H = `h${level}` as "h2" | "h3" | "h4";

  // Keep the latest values for the effects below without making them re-run.
  const latest = useRef({ isOpen, controlled, onToggle, key, waiting });
  latest.current = { isOpen, controlled, onToggle, key, waiting };

  const set = (next: boolean, remember: boolean) => {
    const l = latest.current;
    if (next === l.isOpen) return;
    if (!l.controlled) setOwn(next);
    if (remember && l.key) writeFold(l.key, next, l.waiting);
    l.onToggle?.(next);
  };

  // The same card on another page (one person's page to the next, say) takes that page's remembered choice.
  const keyWas = useRef(key);
  useEffect(() => {
    if (keyWas.current === key) return;
    keyWas.current = key;
    if (!latest.current.controlled) setOwn((key ? readFold(key, latest.current.waiting) : null) ?? fallback);
  }, [key, fallback]);

  // Something starts waiting after the card appeared (its count loaded later): open it, unless the owner closed it
  // while things were waiting.
  const wasWaiting = useRef(waiting);
  useEffect(() => {
    if (waiting && !wasWaiting.current) {
      const l = latest.current;
      set((l.key ? readFold(l.key, true) : null) ?? true, false);
    }
    wasWaiting.current = waiting;
  }, [waiting]);

  // A link to #<id>, or a reveal, opens the card without changing what is remembered.
  const hashHit = Boolean(id && page?.hash === `#${id}`);
  useEffect(() => {
    if (hashHit || reveal) set(true, false);
  }, [hashHit, reveal]);

  const tone_ = tone ?? (waiting ? "ochre" : "neutral");
  const parts = (
    <>
      {icon && <IconTile icon={icon} tone={tone_} size={variant === "row" ? "md" : "lg"} />}
      <span className="fold-text">
        <span className="fold-title">{title}</span>
        {summary && <span className="fold-summary">{summary}</span>}
      </span>
      {chip && <span className="fold-chip">{chip}</span>}
      {meta && <span className="fold-meta">{meta}</span>}
    </>
  );
  const heading = fixed ? (
    <H className="fold-heading">
      <span className="fold-head is-fixed">{parts}</span>
    </H>
  ) : (
    <H className="fold-heading">
      <button type="button" className="fold-head" aria-expanded={isOpen} aria-controls={bodyId} onClick={() => set(!isOpen, true)}>
        {parts}
        <ChevronDown className="fold-chevron" size={18} strokeWidth={1.75} aria-hidden="true" focusable="false" />
      </button>
    </H>
  );
  const classes = ["fold", `fold-${variant}`, isOpen && "is-open", fixed && "is-fixed", waiting && "is-waiting", !icon && "fold-noicon", className].filter(Boolean).join(" ");
  return (
    <section id={id} className={classes}>
      {actions ? (
        <div className="fold-top">
          {heading}
          <div className="fold-head-actions">{actions}</div>
        </div>
      ) : (
        heading
      )}
      <div id={bodyId} className="fold-body" hidden={!isOpen}>
        {children}
      </div>
    </section>
  );
}

export interface CardProps extends Omit<FoldCardProps, "id" | "title" | "summary"> {
  /** Unique on the page; part of the remembered key and the #link that opens it. */
  id: string;
  title: ReactNode;
  /** One line under the title, shown open or closed: the card's state, or what it is, in a few words. */
  subtitle: ReactNode;
}

/**
 * Card: the one card for every card on every page (kit README, "Cards"). A FoldCard with id and subtitle required, so
 * every card folds to its title and subtitle and remembers the owner's choice.
 * Give the page's first card defaultOpen, and any card holding things waiting for the owner waiting.
 */
export function Card({ subtitle, ...rest }: CardProps) {
  return <FoldCard {...rest} summary={subtitle} />;
}

/**
 * FoldList: one card holding a list of FoldCard variant="row" items, with an optional header (title, count chip,
 * a "See all" link). Use it for "Waiting on you" style lists.
 */
export function FoldList({ title, count, action, level = 2, id, children }: { title: ReactNode; count?: number; action?: ReactNode; level?: 2 | 3; id?: string; children: ReactNode }) {
  const H = `h${level}` as "h2" | "h3";
  const auto = useId();
  const headId = `${id ?? auto}-title`;
  return (
    <section className="fold-list" aria-labelledby={headId} id={id}>
      <header className="fold-list-head">
        <H id={headId}>{title}</H>
        {count !== undefined && count > 0 && <span className="pill-count">{count}</span>}
        {action && <span className="fold-list-action">{action}</span>}
      </header>
      <div className="fold-list-body">{children}</div>
    </section>
  );
}
